import { Box, Stack, TablePagination, Typography } from '@mui/material';
import { CancelToken } from 'apisauce';
import React, { useEffect } from 'react';
import { useState } from 'react';
import PageHeader from '../../../components/PageHeader';
import TablePaginationActions from '../../../components/TablePaginationActions';
import VoyageSplitPanel from './Components/VoyageSplitPanel';
import appapi from '../../../api/apiClient';
import { useDispatch, useSelector } from 'react-redux';
import Datatable from '../../../components/DataTable';
import moment from 'moment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ShipIcon from '../../../assets/images/ship.svg';
import { useTranslation } from 'react-i18next';
import DoneIcon from '@mui/icons-material/Done';
import { useTheme } from '@emotion/react';
import { checkPermission } from '../../../redux/features/authSlice';
import AccessDenied from '../AccesDenied/AccessDenied';

export default function Voyage({ data, onBack }) {

  const theme = useTheme();

  const dispatch = useDispatch();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [voyageList, setVoyageList] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [payload, setPayload] = useState({
    page: 1,
    limit: 10,
    filter: null,
    totalLength: 0,
    paginationPage: 0,
    companyTypeFilter: [],
    field: '_id',
    sortOrder: 'desc',
  });

  let source = CancelToken.source();

  const { t } = useTranslation();

  const handleSearchEmit = (search) => {
    setPayload((prevState) => ({ ...prevState, limt: 10, paginationPage: 0, page: 1, filter: search }));
  };

  const handleToggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleChangePage = (event, newPage) => {
    setPayload((prevState) => ({ ...prevState, page: newPage + 1, paginationPage: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPayload((prev) => ({ ...prev, limt: parseInt(event.target.value, 10), paginationPage: 1, page: 1 }));
  };

  const voyagePermission = useSelector((state) => checkPermission(state, 'Fleet Management', 'Manage Voyages'));

  const selectRow = (sitem, sindex) => {
    const users = voyageList.map((item, index) => {
      item.Sno = payload.limit * (payload.page === 1 ? 0 : payload.page - 1) + index + 1;
      item.selected = item._id === sitem._id;
      return item;
    });
    setSelectedData(sitem);
    setVoyageList(users);
    setIsDrawerOpen(true);
  };

  const columns = [
    {
      id: 0,
      header: '#',
      accessor: 'Sno',
      width: '5%',
    },
    {
      id: 1,
      header: 'Start date',
      accessor: 'fromDate',
      width: '8%',
    },
    {
      id: 2,
      header: 'icon',
      accessor: 'dotArrow',
      icon: true,
      width: '3%',
      Cell: (value) => (
        <Stack
          className={`!text-[12px]`}
          sx={{ color: '#006DFF' }}
        >
          <ArrowForwardIcon />
        </Stack>
      ),
    },
    {
      id: 3,
      header: 'End date',
      accessor: 'toDate',
      width: '8%',
      Cell: (values) => (
        <Stack direction={'row'} alignItems={'center'}>
          <Typography className='fs-12 ff-Roboto fw-regular'>{values?.toDate}</Typography>
          {values?.isVerified && (
              <Stack
                style={{ alignItems: 'center', justifyContent: 'center', backgroundColor: '#00A632' }}
                className="h-[12px] w-[12px] rounded-[6px] ml-[5px] "
              >
                <DoneIcon style={{ color: '#ffffff', transform: 'scale(0.5)' }} />
              </Stack>
            )}
        </Stack>
      ),
    },
    {
      id: 4,
      header: 'Voyage from',
      accessor: 'voyageFrom',
      width: '8%',
    },
    {
      id: 5,
      header: 'icon',
      accessor: 'dotArrow',
      icon: true,
      width: '5%',
      jcc: true,
      Cell: (value) => (
        <div className="!justify-center">
          <Stack
            className={`!text-[12px]`}
            sx={{ color: '#006DFF' }}
          >
            <img
              src={ShipIcon}
              style={{ width: '48px' }}
            />
          </Stack>
        </div>
      ),
    },
    {
      id: 6,
      header: 'Voyage to',
      accessor: 'voyageTo',
      width: '10%',
    },
    {
      id: 7,
      header: 'Owner',
      accessor: 'owner',
      width: '10%',
    },
    {
      id: 8,
      header: 'Ship name',
      accessor: 'shipName',
      width: '10%',
    },
    {
      id: 9,
      header: 'Ship type',
      accessor: 'shipType',
      width: '8%',
    },
    {
      id: 10,
      header: 'EUA required',
      accessor: 'totalEstEua',
      width: '8%',
      Cell: (value) => (
        <div className="flex">
          <Typography
            className="fw-regular ff-Roboto fs-12"
            color={theme.palette.primary.text}
          >
            {value?.totalEstEua ? parseFloat(value?.totalEstEua).toFixed(2) : '-'}
          </Typography>
        </div>
      ),
    },
    {
      id: 11,
      header: 'EUA Cost',
      accessor: 'totalEstEuaCost',
      width: '8%',
      Cell: (value) => (
        <div className="flex">
          <Typography
            className="fw-regular ff-Roboto fs-12"
            color={theme.palette.primary.text}
          >
            {value?.totalEstEuaCost ? parseFloat(value?.totalEstEuaCost).toFixed(2) : '-'}
          </Typography>
        </div>
      ),
    },
  ];

  const handleGetVoyageList = async (fromSearch = true) => {
    setIsLoading(true);
    source.cancel();
    source = CancelToken.source();
    const response = await appapi.get(
      `fleet/voyageList`,
      {
        pageNumber: payload.page,
        filter: payload.filter || null,
        pageSize: payload.limit,
        companyTypeFilter: data?.type,
        sortOrderField: payload.field === 'companyName' ? 'sortCompanyName' : payload.field === 'namen' ? 'sortName' : payload.field,
        sortOrder: payload.sortOrder,
        companyContactId: data?.contact?._id,
      },
      { cancelToken: source.token },
    );
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        let userData = data?.response?.response;
        userData.map((item, index) => {
          item.Sno = payload.limit * (payload.page === 1 ? 0 : payload.page - 1) + index + 1;
          item.fromDate = moment(new Date(item.fromDate)).format('DD MMM YYYY');
          item.toDate = moment(new Date(item.toDate)).format('DD MMM YYYY');
          return item;
        });
        setVoyageList(userData);
        setPayload((prev) => ({ ...prev, totalLength: data?.response?.length, paginationPage: payload.page === 1 ? 0 : payload.page - 1 }));
        // dispatch(initGraphData());
        setIsLoading(false);
      } else {
        setVoyageList([]);
        setPayload((prev) => ({ ...prev, totalLength: 0, paginationPage: 0 }));
        setIsLoading(false);
      }
    } else {
      setVoyageList([]);
      setPayload((prev) => ({ ...prev, totalLength: 0, paginationPage: 1 }));
      // setIsLoading(false);
    }
  };

  useEffect(() => {
    if (data && voyagePermission.viewAccess) {
      handleGetVoyageList();
    }
    return () => {
      if (source) {
        source.cancel();
      }
    };
    // eslint-disable-next-line
  }, [payload.page, payload.filter, payload.field, payload.sortOrder, payload.role, data, voyagePermission.viewAccess]);

  return (
   voyagePermission?.viewAccess ? <React.Fragment>
      <Stack style={{ flex: 1 }}>
        <PageHeader
          showBackBtn={true}
          OnBackBtn={onBack}
          handleOnSearchEmit={(search) => {
            handleSearchEmit(search);
          }}
        >
          <Stack
            direction="row"
            style={{ marginLeft: '20px' }}
          ></Stack>
        </PageHeader>
        <Stack
          flexGrow={1}
          className="relative overflow-auto flex-auto  h-0"
        >
          <Datatable
            columns={isDrawerOpen ? columns.filter((res) => res.id < 9) : columns}
            data={voyageList}
            tableHeadBgColor={'#f3f4f7'}
            isLoading={isLoading}
            onClickRow={(item, index) => {
              selectRow(item, index);
            }}
          />
        </Stack>

        <Stack
          sx={{ width: '100%' }}
          className="relative pl-[16px]   inputFormStyle"
        >
          <Box className="flex  justify-end items-center h-[56px] w-[100%] p-[5px]">
            <TablePagination
              sx={{ borderBottom: 0 }}
              rowsPerPageOptions={[]}
              count={payload.totalLength ? payload.totalLength : 0}
              rowsPerPage={payload.limit ? payload.limit : 10}
              page={payload.paginationPage ? payload.paginationPage : 0}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </Box>
        </Stack>
      </Stack>
      <Stack className="">
        {isDrawerOpen && (
          <VoyageSplitPanel
            init={() => handleGetVoyageList()}
            data={selectedData}
            isDrawerOpen={isDrawerOpen}
            toggleDrawer={() => {
              if (selectedData) {
                handleToggleDrawer();
              }
            }}
          />
        )}
      </Stack>
    </React.Fragment>: <Stack style={{ flex: 1 }}>
    <PageHeader
          showBackBtn={true}
          OnBackBtn={onBack}
          showSearchBar={false}

        >
          <Stack
            direction="row"
            style={{ marginLeft: '20px' }}
          ></Stack>
        </PageHeader>
      <AccessDenied />
    </Stack>
  );
}
